import React from 'react';
import { RefreshCw, Shield, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatPnlParts } from '../../../utils/positionDisplay';
import { CHIP_DENSE, CHIP_METADATA } from '../../../utils/chipColors';

export interface BasketModalHeaderProps {
  basketName: string;
  strategyCount: number;
  legTotal: number;
  pills: string[];
  filled: number;
  pending: number;
  rejected: number;
  cancelled: number;
  openCount: number;
  closedCount: number;
  realized: number;
  unrealized: number;
  slPlaced: number;
  slPending: number;
  slRejected: number;
  onClose: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}

// Basket-modal fill-progress pill. Bg + text from canonical CHIP_DENSE;
// border classes added per-variant since this surface uses a thin border
// in addition to the bg. Mirrors the identical pattern in StrategyModalHeader.
const FILL_CLS: Record<'green' | 'yellow' | 'red', string> = {
  green: `${CHIP_DENSE.COMPLETE} border-green-200 dark:border-green-800`,
  yellow: `${CHIP_DENSE.TRIGGER_PENDING} border-yellow-200 dark:border-yellow-800`,
  red: `${CHIP_DENSE.REJECTED} border-red-200 dark:border-red-800`,
};

/**
 * Shared header + status ribbon for the basket-level modal used by both
 * Classic and Pro schedule views. Owns the identity block (label, name,
 * counts, pills, Total P&L, close) and a status ribbon (fill progress, open
 * vs closed counts, SL shield, Realized / Unrealized P&L).
 */
export const BasketModalHeader: React.FC<BasketModalHeaderProps> = ({
  basketName, strategyCount, legTotal, pills,
  filled, pending, rejected, cancelled, openCount, closedCount,
  realized, unrealized, slPlaced, slPending, slRejected,
  onClose, onRefresh, refreshing = false,
}) => {
  const totalPnl = formatPnlParts(realized + unrealized);
  const realizedFmt = formatPnlParts(realized);
  const unrealizedFmt = formatPnlParts(unrealized);

  const fillVariant: 'green' | 'yellow' | 'red' =
    rejected > 0 ? 'red' : pending > 0 ? 'yellow' : 'green';
  const fillLabel = rejected > 0
    ? `${rejected}/${legTotal} rejected`
    : pending > 0
      ? (filled > 0
        ? `${filled}/${legTotal} filled · ${pending} pending`
        : `${pending}/${legTotal} pending`)
      : `${filled}/${legTotal} filled`;

  const slLive = slPlaced + slPending + slRejected;

  return (
    <>
      {/* Identity block */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">
              Basket
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate mt-0.5">
              {basketName || '—'}
            </h3>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">
              {strategyCount} {strategyCount === 1 ? 'strategy' : 'strategies'} · {legTotal} legs
            </div>
            {pills.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {pills.map((p) => (
                  <span
                    key={p}
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider',
                      p === 'BTST' ? CHIP_METADATA.BTST : CHIP_METADATA.DEFAULT,
                    )}
                  >
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total P&amp;L</div>
              <div className={cn('text-base font-bold tabular-nums', totalPnl.className)}>{totalPnl.text}</div>
            </div>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Status ribbon */}
      <div className="px-5 py-3 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 flex-wrap text-xs">
        <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md font-semibold border', FILL_CLS[fillVariant])}>
          {fillLabel}
        </span>
        {cancelled > 0 && (
          <span className="text-gray-500 dark:text-gray-400 tabular-nums">
            · {cancelled} cancelled
          </span>
        )}
        <span className="text-gray-600 dark:text-gray-300 tabular-nums">
          {openCount} open · {closedCount} closed
        </span>
        {slLive > 0 && (
          <span className="flex items-center gap-1">
            <Shield className={cn(
              'w-3.5 h-3.5',
              slRejected > 0 ? 'text-red-500' : slPending > 0 ? 'text-yellow-500' : 'text-green-500',
            )} />
            <span className="text-gray-600 dark:text-gray-300 tabular-nums">
              SL {slPlaced}/{slLive}
              {slRejected > 0 && <span className="text-red-500 ml-1">· {slRejected} failed</span>}
              {slPending > 0 && slRejected === 0 && <span className="text-yellow-600 ml-1">· {slPending} pending</span>}
            </span>
          </span>
        )}
        <span className="tabular-nums ml-auto">
          <span className="text-gray-500 dark:text-gray-400">Realized </span>
          <span className={cn('font-medium', realizedFmt.className)}>{realizedFmt.text}</span>
          <span className="text-gray-500 dark:text-gray-400"> · Unrealized </span>
          <span className={cn('font-medium', unrealizedFmt.className)}>{unrealizedFmt.text}</span>
        </span>
      </div>
    </>
  );
};

export default BasketModalHeader;
